import { Client } from '@langchain/langgraph-sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ChatMessage } from '~/modules/music-iq/pages/chatbot-page/types';

// @TODO WMG1-936 - Change to what we need for AWS Bedrock.
const GRAPH_ID = 'marketing_chatbot';
const LANGGRAPH_API_URL =
  'http://internal-im-agents-lb-dev-1743894815.us-east-1.elb.amazonaws.com:8123';

// Time in ms to wait before showing the "thinking" indicator. This will be reset
// every time we start receiving chunks from the back end.
const THINKING_TIMEOUT_MS = 1000;

export function useMusicIqChatbot() {
  const { t } = useTranslation('data', { keyPrefix: 'musicIq' });
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const [error, setError] = useState<Error | null>(null);
  const [threadId, setThreadId] = useState<string | null>(null);

  const clientRef = useRef<Client | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const thinkingTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  // We need two separate states here for tracking while a question is in flight.
  //
  // isThinking can toggle on/off several times while the back end is streaming
  // us responses to the question. We set isThinking on a timer because we always
  // want the user to know the back end is "thinking" while waiting for the next
  // set of response streams.
  //
  // isProcessingQuestion toggles only once for the entire question interaction
  // with the back end. So it goes to true when the user asks a question and then
  // is set to false either when the user aborts while waiting or the back end
  // tells us the stream is complete.
  //
  // isProcessingQuestion is used in the <ChatbotPrompter /> to disable all controls
  // while the question is in flight. If we used isThinking, those controls would
  // enable/disable multiple times during the question, which is not what we want.
  const [isThinking, setIsThinking] = useState(false);
  const [isProcessingQuestion, setIsProcessingQuestion] = useState(false);

  // Make sure to clean up the timer on unmount.
  useEffect(() => {
    clearTimeout(thinkingTimeoutRef.current);
  }, []);

  // ----------------------------------------------------------------------------
  //
  // !!!!! NOTE !!!!!
  //
  // The back end implementation with LangGraph is going away and will be replaced
  // by AWS Bedrock. So the implementation in this hook will have to change once
  // the back end is updated. For now, to develop the front end, we can still use
  // the LangGraph implementation.
  //
  // ----------------------------------------------------------------------------

  // Initialize client
  useEffect(() => {
    clientRef.current = new Client({ apiUrl: LANGGRAPH_API_URL });
  }, []);

  // Create thread on mount
  useEffect(() => {
    async function createThread() {
      if (!clientRef.current) {
        return;
      }

      try {
        const thread = await clientRef.current.threads.create();

        setThreadId(thread.thread_id);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error('Failed to create thread'),
        );

        console.error('Failed to create thread:', err);
      }
    }

    createThread();
  }, []);

  const abort = useCallback(() => {
    if (thinkingTimeoutRef.current) {
      clearTimeout(thinkingTimeoutRef.current);
      thinkingTimeoutRef.current = undefined;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsThinking(false);
      setIsProcessingQuestion(false);

      // Add user message immediately to show the question was stopped.
      setMessages((prev) => [
        ...prev,
        {
          id: `user-${Date.now()}`,
          type: 'user',
          wasAborted: true,
          content: t('chatbot.stoppedResponse'),
        },
      ]);
    }
  }, [t]);

  const ask = useCallback(
    async (content: string) => {
      if (!clientRef.current || !threadId) {
        setError(new Error('Chat not initialized'));

        return;
      }

      const userMessageId = `user-${Date.now()}`;
      const assistantMessageId = `assistant-${Date.now()}`;

      // Add user message immediately
      setMessages((prev) => [
        ...prev,
        { id: userMessageId, type: 'user', content },
      ]);

      setIsThinking(true);
      setError(null);

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();

      const { signal } = abortControllerRef.current;

      setIsProcessingQuestion(true);

      try {
        // Add empty assistant message that will be updated with streamed content
        setMessages((prev) => [
          ...prev,
          { id: assistantMessageId, type: 'chatbot', content: '' },
        ]);

        const streamResponse = clientRef.current.runs.stream(
          threadId,
          GRAPH_ID,
          {
            input: { messages: [{ role: 'human', content }] },
            streamMode: 'messages-tuple',
            signal,
          },
        );

        let accumulatedContent = '';

        for await (const chunk of streamResponse) {
          if (chunk.event === 'messages' && Array.isArray(chunk.data)) {
            const [message] = chunk.data;
            // @TODO WMG1-936 - The casting will be refactored when we switch to AWS Bedrock.
            const textPiece = (message?.content?.[0] as { text: string })?.text;

            if (
              // @TODO WMG1-936 - Ask back end why not 'ai' (according to langgraph).
              // But might not be relevant when using AWS Bedrock.
              // message?.type === 'AIMessageChunk' &&
              typeof textPiece === 'string'
            ) {
              // Content received, no longer "thinking"
              setIsThinking(false);

              // Reset the thinking timeout - if no chunk arrives within the
              // threshold, we'll show the thinking indicator again
              if (thinkingTimeoutRef.current) {
                clearTimeout(thinkingTimeoutRef.current);
              }

              thinkingTimeoutRef.current = setTimeout(() => {
                setIsThinking(true);
              }, THINKING_TIMEOUT_MS);

              accumulatedContent += textPiece;

              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId
                    ? { ...msg, content: accumulatedContent }
                    : msg,
                ),
              );
            }
          }
        }
      } catch (err) {
        // Don't show error if the request was aborted by the user
        if (err instanceof Error && err.name === 'AbortError') {
          console.error('Request aborted by user:', err);

          return;
        }

        setError(
          err instanceof Error ? err : new Error('Failed to send message'),
        );

        // Remove the empty assistant message on error
        setMessages((prev) =>
          prev.filter((msg) => msg.id !== assistantMessageId),
        );

        console.error('Failed to send message:', err);
      } finally {
        if (thinkingTimeoutRef.current) {
          clearTimeout(thinkingTimeoutRef.current);
          thinkingTimeoutRef.current = undefined;
        }

        abortControllerRef.current = null;
        setIsThinking(false);
        setIsProcessingQuestion(false);
      }
    },
    [threadId],
  );

  return { messages, ask, abort, isThinking, isProcessingQuestion, error };
}
